import { useState } from "react";
import { X } from "lucide-react";
import { CONSUMER_EMAIL_DOMAINS, type ContactAccess } from "@telecloud/shared";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

export const NEW_ORG = "__new__";

export interface StaffOrg {
  id: string;
  name: string;
  adminDomains: string[];
}

export interface ContactEntry {
  email: string;
  /** Unset until staff choose; the effective access is then the domain default. */
  access?: ContactAccess;
}

export interface ProjectOrgState {
  /** An existing org id, NEW_ORG, or "" before a choice. */
  orgId: string;
  newOrgName: string;
  mainContactEmail: string;
  contacts: ContactEntry[];
}

export const emptyProjectOrgState: ProjectOrgState = {
  orgId: "",
  newOrgName: "",
  mainContactEmail: "",
  contacts: [],
};

const ACCESS_OPTIONS: Array<{ value: ContactAccess; label: string }> = [
  { value: "organization", label: "Organization" },
  { value: "project", label: "This project" },
];

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

function domainOf(email: string): string {
  return normalizeEmail(email).split("@")[1] ?? "";
}

/**
 * Default access for a contact (SP1 §4.4): Organization when their email domain equals
 * an org admin's domain, else This project. Consumer domains (gmail.com, …) never match.
 * Purely a UI default.
 */
export function defaultContactAccess(email: string, adminDomains: string[]): ContactAccess {
  const domain = domainOf(email);
  if (!domain || CONSUMER_EMAIL_DOMAINS.has(domain)) return "project";
  return adminDomains.includes(domain) ? "organization" : "project";
}

/** Org admin domains for the current choice: the main contact's (new org) or the org's. */
export function adminDomainsFor(state: ProjectOrgState, orgs: StaffOrg[]): string[] {
  if (state.orgId === NEW_ORG) {
    const domain = domainOf(state.mainContactEmail);
    return domain && !CONSUMER_EMAIL_DOMAINS.has(domain) ? [domain] : [];
  }
  return orgs.find((o) => o.id === state.orgId)?.adminDomains ?? [];
}

export function effectiveContacts(
  state: ProjectOrgState,
  orgs: StaffOrg[]
): Array<{ email: string; access: ContactAccess }> {
  const domains = adminDomainsFor(state, orgs);
  return state.contacts.map((c) => ({
    email: c.email,
    access: c.access ?? defaultContactAccess(c.email, domains),
  }));
}

/** The `org` part of projects.create, or null while the choice is incomplete. */
export function orgInput(
  state: ProjectOrgState
):
  | { kind: "existing"; tenantId: string }
  | { kind: "new"; name: string; mainContact: { email: string } }
  | null {
  if (state.orgId === NEW_ORG) {
    const name = state.newOrgName.trim();
    const email = normalizeEmail(state.mainContactEmail);
    return name && email ? { kind: "new", name, mainContact: { email } } : null;
  }
  return state.orgId ? { kind: "existing", tenantId: state.orgId } : null;
}

interface ProjectOrgFieldsProps {
  orgs: StaffOrg[];
  value: ProjectOrgState;
  onChange: (value: ProjectOrgState) => void;
}

/** Organization picker, main contact for a new org, and contacts with per-person access. */
export function ProjectOrgFields({ orgs, value, onChange }: ProjectOrgFieldsProps) {
  const [contactEmail, setContactEmail] = useState("");
  const contacts = effectiveContacts(value, orgs);
  const isNew = value.orgId === NEW_ORG;

  const addContact = () => {
    const email = normalizeEmail(contactEmail);
    if (!email) return;
    const isMain = isNew && email === normalizeEmail(value.mainContactEmail);
    if (!isMain && !value.contacts.some((c) => c.email === email)) {
      onChange({ ...value, contacts: [...value.contacts, { email }] });
    }
    setContactEmail("");
  };

  const setAccess = (email: string, access: ContactAccess) =>
    onChange({
      ...value,
      contacts: value.contacts.map((c) => (c.email === email ? { ...c, access } : c)),
    });

  const removeContact = (email: string) =>
    onChange({ ...value, contacts: value.contacts.filter((c) => c.email !== email) });

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="projectOrg">Organization</Label>
        <Select value={value.orgId} onValueChange={(orgId) => onChange({ ...value, orgId })}>
          <SelectTrigger id="projectOrg" aria-label="Organization">
            <SelectValue placeholder="Choose an organization" />
          </SelectTrigger>
          <SelectContent>
            {orgs.map((org) => (
              <SelectItem key={org.id} value={org.id}>
                {org.name}
              </SelectItem>
            ))}
            {orgs.length > 0 && <SelectSeparator />}
            <SelectItem value={NEW_ORG}>New organization</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isNew && (
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="newOrgName">Organization name</Label>
            <Input
              id="newOrgName"
              placeholder="Acme Plumbing, Inc."
              value={value.newOrgName}
              onChange={(e) => onChange({ ...value, newOrgName: e.target.value })}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="mainContactEmail">Main contact</Label>
            <Input
              id="mainContactEmail"
              type="email"
              placeholder="owner@acme.com"
              value={value.mainContactEmail}
              onChange={(e) => onChange({ ...value, mainContactEmail: e.target.value })}
              required
            />
          </div>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="contactEmail">Contact email</Label>
        <div className="flex gap-2">
          <Input
            id="contactEmail"
            type="email"
            placeholder="john@acme.com"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addContact();
              }
            }}
          />
          <Button type="button" variant="outline" onClick={addContact} disabled={!contactEmail}>
            Add
          </Button>
        </div>
      </div>

      {contacts.length > 0 && (
        <div className="space-y-2">
          <Label>Contacts ({contacts.length})</Label>
          <ul className="divide-y rounded-md border">
            {contacts.map((contact) => (
              <li key={contact.email} className="flex items-center gap-3 px-3 py-2">
                <span className="min-w-0 flex-1 truncate text-sm">{contact.email}</span>
                <div
                  role="group"
                  aria-label={`Access for ${contact.email}`}
                  className="flex gap-1"
                >
                  {ACCESS_OPTIONS.map((option) => {
                    const pressed = contact.access === option.value;
                    return (
                      <Button
                        key={option.value}
                        type="button"
                        aria-pressed={pressed}
                        variant={pressed ? "default" : "outline"}
                        size="sm"
                        onClick={() => setAccess(contact.email, option.value)}
                      >
                        {option.label}
                      </Button>
                    );
                  })}
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={`Remove ${contact.email}`}
                  onClick={() => removeContact(contact.email)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
